import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SuperAdminSidebar from '@/components/sidebar/superAdminSidebar';
import { SuperAdminMobileHeader } from '@/components/navigation/SuperAdminMobileHeader';
import { NotificationBell } from '@/components/NotificationBell';
import { 
  Users, Award, Search, Plus, Building2, Mail, ExternalLink, 
  CheckCircle2, Clock, X, Shield, Filter, RefreshCw, Briefcase, ChevronRight, AlertCircle, Check,
  Trash2, Ban, AlertTriangle, UserX, ShieldAlert
} from 'lucide-react';
import logoImg from '@/assets/Sthitih_Logo.png';
import { SuperAdminService } from '@/services/superadmin.service';
import { toast, Toaster } from 'react-hot-toast';

interface SthitihMentorData {
  id: string;
  display_name: string;
  email: string;
  avatar_url?: string;
  company?: string;
  designation?: string;
  bio?: string;
  contact_number?: string;
  linkedin_github_url?: string;
  mentorship_areas?: string[];
  skills?: string[];
  role: string;
  mentor_type: string;
  created_at: string;
  granted_clients: Array<{
    grant_id: string;
    client_id: string;
    client_name: string;
    client_code: string;
    assigned_at: string;
  }>;
  allocated_teams_count: number;
}

interface PendingInviteData {
  id: string;
  email: string;
  name: string;
  role: string;
  status: string;
  token: string;
  created_at: string;
  client_id?: string;
  days_remaining?: number;
}

interface BlacklistedEmailData {
  id: string;
  email: string;
  reason: string | null;
  blacklisted_by: string;
  created_at: string;
}

const SthitihMentorsView: React.FC = () => {
  const navigate = useNavigate();
  const [mentors, setMentors] = useState<SthitihMentorData[]>([]);
  const [pendingInvites, setPendingInvites] = useState<PendingInviteData[]>([]);
  const [blacklistedEmails, setBlacklistedEmails] = useState<BlacklistedEmailData[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'active' | 'invites' | 'blacklist'>('active');

  // Invite Mentor Modal State
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);
  const [submittingInvite, setSubmittingInvite] = useState(false);

  // Grant Access Modal State
  const [isGrantModalOpen, setIsGrantModalOpen] = useState(false);
  const [selectedMentor, setSelectedMentor] = useState<SthitihMentorData | null>(null);
  const [mentorGrantedClientIds, setMentorGrantedClientIds] = useState<string[]>([]);
  const [submittingGrant, setSubmittingGrant] = useState(false);
  const [clientSearchQuery, setClientSearchQuery] = useState('');

  // Blacklist Modal State
  const [isBlacklistModalOpen, setIsBlacklistModalOpen] = useState(false);
  const [blacklistEmailInput, setBlacklistEmailInput] = useState('');
  const [blacklistReasonInput, setBlacklistReasonInput] = useState('');
  const [submittingBlacklist, setSubmittingBlacklist] = useState(false);

  // Revoke Invite State
  const [invitationToRevoke, setInvitationToRevoke] = useState<PendingInviteData | null>(null);
  const [submittingRevoke, setSubmittingRevoke] = useState(false);

  // Remove Mentor State
  const [mentorToRemove, setMentorToRemove] = useState<SthitihMentorData | null>(null);
  const [submittingRemoveMentor, setSubmittingRemoveMentor] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      const [mentorRes, clientsRes, blacklistRes] = await Promise.all([
        SuperAdminService.getSthitihMentors(),
        SuperAdminService.getClients().catch(() => []),
        SuperAdminService.getBlacklistedEmails().catch(() => []),
      ]);

      const rawMentorList = Array.isArray(mentorRes)
        ? mentorRes.filter((m: any) => m.status !== 'pending')
        : (mentorRes?.mentors || []);
      const mentorList = rawMentorList.map((m: any) => ({
        ...m,
        granted_clients: Array.isArray(m.granted_clients)
          ? m.granted_clients
          : Array.isArray(m.granted_institutes)
          ? m.granted_institutes.map((gi: any) => ({
              grant_id: gi.id,
              client_id: gi.id,
              client_name: gi.name,
              client_code: gi.client_code || gi.subdomain || gi.name,
              assigned_at: gi.granted_at,
            }))
          : [],
      }));
      const inviteList = Array.isArray(mentorRes)
        ? mentorRes.filter((m: any) => m.status === 'pending')
        : (mentorRes?.pending_invitations || []);

      setMentors(mentorList);
      setPendingInvites(inviteList);
      setBlacklistedEmails(Array.isArray(blacklistRes) ? blacklistRes : []);
      setClients(clientsRes || []);
    } catch (err: any) {
      console.error('Failed to load Sthitih Mentors:', err);
      toast.error('Failed to load mentors data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteName.trim() || !inviteEmail.trim()) {
      toast.error('Name and Email are required');
      return;
    }

    try {
      setSubmittingInvite(true);
      await SuperAdminService.inviteSthitihMentor({
        name: inviteName.trim(),
        email: inviteEmail.trim(),
        client_ids: selectedClientIds,
        message: inviteMessage.trim() || undefined,
        mentor_type: 'industry',
      });

      toast.success(`🎉 Invitation sent to ${inviteEmail.trim()}`);
      setIsInviteModalOpen(false);
      setInviteName('');
      setInviteEmail('');
      setInviteMessage('');
      setSelectedClientIds([]);
      loadData();
    } catch (err: any) {
      console.error('Invite failed:', err);
      toast.error(err.response?.data?.message || 'Failed to send invitation');
    } finally {
      setSubmittingInvite(false);
    }
  };

  const handleOpenGrantModal = (mentor: SthitihMentorData) => {
    setSelectedMentor(mentor);
    setMentorGrantedClientIds((mentor.granted_clients || []).map(c => c.client_id));
    setClientSearchQuery('');
    setIsGrantModalOpen(true);
  };

  const handleToggleClientGrant = (clientId: string) => {
    setMentorGrantedClientIds(prev => 
      prev.includes(clientId) ? prev.filter(id => id !== clientId) : [...prev, clientId]
    );
  };

  const handleSaveGrantInstitutes = async () => {
    if (!selectedMentor) return;
    try {
      setSubmittingGrant(true);
      await SuperAdminService.grantMentorInstitutes(selectedMentor.id, mentorGrantedClientIds);
      toast.success(`Updated institution access for ${selectedMentor.display_name}`);
      setIsGrantModalOpen(false);
      loadData();
    } catch (err: any) {
      console.error('Failed to grant institutes:', err);
      toast.error(err.response?.data?.message || 'Failed to update access');
    } finally {
      setSubmittingGrant(false);
    }
  };

  const filteredMentors = mentors.filter(m => {
    const q = searchQuery.toLowerCase();
    const nameMatch = (m.display_name || '').toLowerCase().includes(q);
    const emailMatch = (m.email || '').toLowerCase().includes(q);
    const companyMatch = (m.company || '').toLowerCase().includes(q);
    const designationMatch = (m.designation || '').toLowerCase().includes(q);
    const tagMatch = (m.mentorship_areas || []).some(tag => tag.toLowerCase().includes(q));
    return nameMatch || emailMatch || companyMatch || designationMatch || tagMatch;
  });

  const filteredInvites = pendingInvites.filter(inv => {
    const q = searchQuery.toLowerCase();
    return (inv.name || '').toLowerCase().includes(q) || (inv.email || '').toLowerCase().includes(q);
  });

  const filteredBlacklist = blacklistedEmails.filter(b => {
    const q = searchQuery.toLowerCase();
    return (b.email || '').toLowerCase().includes(q) || (b.reason || '').toLowerCase().includes(q);
  });

  const handleRevokeInvite = async () => {
    if (!invitationToRevoke) return;
    try {
      setSubmittingRevoke(true);
      await SuperAdminService.revokeSthitihMentorInvitation(invitationToRevoke.id);
      toast.success(`Revoked invitation for ${invitationToRevoke.email}`);
      setInvitationToRevoke(null);
      loadData();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to revoke invitation');
    } finally {
      setSubmittingRevoke(false);
    }
  };

  const handleRemoveMentor = async () => {
    if (!mentorToRemove) return;
    try {
      setSubmittingRemoveMentor(true);
      await SuperAdminService.removeSthitihMentor(mentorToRemove.id);
      toast.success(`Removed ${mentorToRemove.display_name} from Sthitih Mentors`);
      setMentorToRemove(null);
      loadData();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to remove mentor');
    } finally {
      setSubmittingRemoveMentor(false);
    }
  };

  const handleBlacklistSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!blacklistEmailInput.trim()) {
      toast.error('Email address is required to blacklist');
      return;
    }
    try {
      setSubmittingBlacklist(true);
      await SuperAdminService.blacklistEmail({
        email: blacklistEmailInput.trim(),
        reason: blacklistReasonInput.trim() || undefined,
      });
      toast.success(`Blacklisted ${blacklistEmailInput.trim()}`);
      setIsBlacklistModalOpen(false);
      setBlacklistEmailInput('');
      setBlacklistReasonInput('');
      loadData();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to blacklist email');
    } finally {
      setSubmittingBlacklist(false);
    }
  };

  const handleRemoveBlacklist = async (id: string, email: string) => {
    if (!window.confirm(`Are you sure you want to unblock ${email}?`)) return;
    try {
      await SuperAdminService.removeBlacklist(id);
      toast.success(`Unblocked ${email}`);
      loadData();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to remove blacklist entry');
    }
  };



  return (
    <div className="min-h-screen bg-[#F7F2EA] text-[#243238] flex w-full relative">
      <Toaster position="top-right" />
      <SuperAdminSidebar />

      <div className="flex-1 flex flex-col pl-0 pb-24 md:pl-20 md:pb-8 min-h-screen min-w-0 w-full relative">
        <SuperAdminMobileHeader title="Sthitih Mentors" />

        {/* Sleek blended desktop header */}
        <header className="hidden md:flex h-16 px-8 bg-[#F7F2EA] border-b border-[#DDD0BE] items-center justify-between sticky top-0 z-20 w-full">
          <div className="flex items-center gap-3">
            <span className="text-[10px] font-bold uppercase px-3 py-1 rounded-xl bg-[#1F5A43]/10 text-[#1F5A43] border border-[#1F5A43]/20 tracking-wider shadow-xs flex items-center gap-1.5">
              <Award className="w-3.5 h-3.5 text-[#1F5A43]" />
              Platform Industry Mentors Ecosystem
            </span>
          </div>
          <div className="flex items-center gap-4 select-none">
            <NotificationBell type="superadmin" />
            <img
              src={logoImg}
              alt="Sthitih"
              className="h-10 w-auto object-contain rounded-xl shadow-xs border border-black/5 hover:scale-105 transition-transform"
            />
          </div>
        </header>

        {/* Main Content */}
        <main className="p-4 sm:p-8 max-w-[1600px] mx-auto w-full space-y-8 relative">
          
          {/* Top Banner & Action */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-[#FFFDF9] border border-[#E4D8C8] rounded-2xl p-6 shadow-earth">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-300">
                  Global Mentorship Pool
                </span>
                <span className="text-xs text-[#8D8A83] font-mono">STHITIH-MENTOR-v1</span>
              </div>
              <h1 className="text-xl font-black text-[#243238]">Sthitih Mentors Directory</h1>
              <p className="text-xs text-[#6F706C] mt-1 max-w-2xl">
                Onboard elite industry leaders and technical mentors to Sthitih. Grant their mentorship capabilities to selected client institutions, allowing campus event coordinators to allocate hackathon teams directly to them.
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  setBlacklistEmailInput('');
                  setBlacklistReasonInput('');
                  setIsBlacklistModalOpen(true);
                }}
                className="px-4 py-2.5 rounded-xl border border-[#DDD0BE] bg-[#F7F2EA] text-[#243238] text-xs font-bold uppercase tracking-wider hover:bg-[#EFE5D6] transition-all flex items-center gap-2 shrink-0 cursor-pointer"
              >
                <Ban className="w-3.5 h-3.5 text-red-600" />
                Blacklist Email
              </button>
              <button
                onClick={() => setIsInviteModalOpen(true)}
                className="px-5 py-2.5 rounded-xl bg-[#1F5A43] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#184735] transition-all shadow-earth flex items-center gap-2 shrink-0 cursor-pointer"
              >
                <Plus className="w-4 h-4" />
                Invite Sthitih Mentor
              </button>
            </div>
          </div>



          {/* Search, Filter & Tabs Bar */}
          <div className="bg-[#FFFDF9] border border-[#E4D8C8] rounded-2xl p-4 shadow-earth flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setActiveTab('active')}
                className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                  activeTab === 'active'
                    ? 'bg-[#1F5A43] text-white shadow-xs'
                    : 'text-[#6F706C] hover:bg-[#F7F2EA]'
                }`}
              >
                Active Sthitih Mentors ({mentors.length})
              </button>
              <button
                onClick={() => setActiveTab('invites')}
                className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                  activeTab === 'invites'
                    ? 'bg-[#1F5A43] text-white shadow-xs'
                    : 'text-[#6F706C] hover:bg-[#F7F2EA]'
                }`}
              >
                Pending Invitations ({pendingInvites.length})
              </button>
              <button
                onClick={() => setActiveTab('blacklist')}
                className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                  activeTab === 'blacklist'
                    ? 'bg-red-700 text-white shadow-xs'
                    : 'text-[#6F706C] hover:bg-[#F7F2EA]'
                }`}
              >
                Blacklisted Emails ({blacklistedEmails.length})
              </button>
            </div>

            <div className="flex items-center gap-3">
              <div className="relative w-full md:w-80">
                <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8D8A83]" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by mentor, company, skills, or tags..."
                  className="w-full pl-10 pr-4 py-2 bg-[#F7F2EA] border border-[#DDD0BE] rounded-xl text-xs text-[#243238] placeholder-[#8D8A83] focus:outline-none focus:border-[#1F5A43]"
                />
              </div>
              <button
                onClick={loadData}
                title="Refresh"
                className="p-2.5 rounded-xl border border-[#DDD0BE] bg-[#F7F2EA] text-[#6F706C] hover:text-[#1F5A43] hover:bg-[#FFFDF9] transition-all cursor-pointer"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Mentors Cards Grid */}
          {activeTab === 'active' ? (
            filteredMentors.length === 0 ? (
              <div className="bg-[#FFFDF9] border border-[#E4D8C8] rounded-2xl p-12 text-center shadow-earth">
                <Award className="w-12 h-12 text-[#DDD0BE] mx-auto mb-3" />
                <h3 className="text-sm font-bold text-[#243238]">No Sthitih Mentors Found</h3>
                <p className="text-xs text-[#6F706C] mt-1 max-w-sm mx-auto">
                  {searchQuery ? 'No mentors matched your search query.' : 'No global industry mentors have been onboarded yet.'}
                </p>
                {!searchQuery && (
                  <button
                    onClick={() => setIsInviteModalOpen(true)}
                    className="mt-4 px-4 py-2 rounded-xl bg-[#1F5A43] text-white text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 cursor-pointer"
                  >
                    <Plus className="w-4 h-4" /> Invite First Sthitih Mentor
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredMentors.map((mentor) => (
                  <div
                    key={mentor.id}
                    className="bg-[#FFFDF9] border border-[#E4D8C8] rounded-2xl p-4 shadow-earth hover:border-[#1F5A43]/40 transition-all flex flex-col justify-between"
                  >
                    <div>
                      {/* Top Mentor Meta */}
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex items-center gap-3 min-w-0">
                          {mentor.avatar_url ? (
                            <img
                              src={mentor.avatar_url}
                              alt={mentor.display_name}
                              className="w-11 h-11 rounded-xl object-cover border border-[#DDD0BE] shadow-xs shrink-0"
                            />
                          ) : (
                            <div className="w-11 h-11 rounded-xl bg-[#1F5A43]/10 border border-[#1F5A43]/20 text-[#1F5A43] font-black text-base flex items-center justify-center shadow-xs shrink-0">
                              {(mentor.display_name || mentor.email || 'M').charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <h3 className="text-sm font-bold text-[#243238] truncate">{mentor.display_name}</h3>
                              <span className="px-1.5 py-0.5 rounded-md text-[8px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-300 shrink-0">
                                Sthitih
                              </span>
                            </div>
                            <p className="text-[11px] font-semibold text-[#1F5A43] mt-0.5 flex items-center gap-1 truncate">
                              <Briefcase className="w-3 h-3 text-[#1F5A43] shrink-0" />
                              <span className="truncate">{mentor.designation || 'Industry Specialist'} {mentor.company ? `• ${mentor.company}` : ''}</span>
                            </p>
                            <p className="text-[10px] text-[#8D8A83] truncate">{mentor.email}</p>
                          </div>
                        </div>

                        {mentor.linkedin_github_url && (
                          <a
                            href={mentor.linkedin_github_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 rounded-lg bg-[#F7F2EA] border border-[#DDD0BE] text-[#6F706C] hover:text-[#1F5A43] hover:bg-[#FFFDF9] transition-all shrink-0"
                            title="LinkedIn Profile"
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        )}
                      </div>

                      {/* Bio */}
                      {mentor.bio && (
                        <p className="text-[11px] text-[#5A4634] leading-relaxed mb-3 bg-[#F7F2EA]/60 p-2.5 rounded-xl border border-[#DDD0BE]/60 line-clamp-2">
                          {mentor.bio}
                        </p>
                      )}

                      {/* Domain Expertise Tags */}
                      {((mentor.mentorship_areas && mentor.mentorship_areas.length > 0) || (mentor.skills && mentor.skills.length > 0)) && (
                        <div className="mb-3">
                          <span className="text-[9px] font-bold text-[#8D8A83] uppercase tracking-wider block mb-1">
                            Expertise
                          </span>
                          <div className="flex flex-wrap gap-1">
                            {[...(mentor.mentorship_areas || []), ...(mentor.skills || [])].slice(0, 4).map((tag, idx) => (
                              <span
                                key={idx}
                                className="px-2 py-0.5 rounded-md text-[9px] font-medium bg-[#EFE5D6] text-[#5A4634] border border-[#DDD0BE]"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Granted Institutions Summary */}
                      <div className="mb-3 pt-2.5 border-t border-[#E4D8C8]">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[9px] font-bold text-[#8D8A83] uppercase tracking-wider flex items-center gap-1">
                            <Building2 className="w-3 h-3 text-[#C47C14]" />
                            Institutes ({mentor.granted_clients.length})
                          </span>
                          <span className="text-[9px] font-bold text-[#1F5A43]">
                            {mentor.allocated_teams_count} Team(s) Allocated
                          </span>
                        </div>

                        {(mentor.granted_clients || []).length === 0 ? (
                          <div className="text-[10px] text-[#8D8A83] italic bg-[#F7F2EA] p-2 rounded-lg border border-dashed border-[#DDD0BE]">
                            Not granted to any institutions.
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-1 max-h-16 overflow-y-auto">
                            {(mentor.granted_clients || []).map((gc) => (
                              <span
                                key={gc.client_id}
                                className="px-2 py-0.5 rounded-lg text-[10px] font-semibold bg-[#DFF5E7] text-[#1F7A4F] border border-[#1F7A4F]/20 flex items-center gap-1"
                              >
                                <Check className="w-2.5 h-2.5 text-[#1F7A4F]" />
                                {gc.client_name || gc.client_code}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Footer Actions */}
                    <div className="pt-3 border-t border-[#E4D8C8] flex items-center justify-between gap-2">
                      <span className="text-[9px] text-[#8D8A83]">
                        {new Date(mentor.created_at).toLocaleDateString()}
                      </span>

                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => handleOpenGrantModal(mentor)}
                          className="px-2.5 py-1 rounded-lg bg-[#F7F2EA] hover:bg-[#EFE5D6] border border-[#DDD0BE] text-[#243238] text-[11px] font-bold transition-all flex items-center gap-1 cursor-pointer"
                        >
                          <Building2 className="w-3 h-3 text-[#1F5A43]" />
                          Access
                        </button>
                        <button
                          onClick={() => setMentorToRemove(mentor)}
                          className="px-2.5 py-1 rounded-lg bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-800 text-[11px] font-bold transition-all flex items-center gap-1 cursor-pointer"
                          title="Remove from Sthitih Mentors"
                        >
                          <UserX className="w-3 h-3 text-amber-700" />
                          Remove
                        </button>
                        <button
                          onClick={() => {
                            setBlacklistEmailInput(mentor.email);
                            setBlacklistReasonInput('');
                            setIsBlacklistModalOpen(true);
                          }}
                          className="px-2.5 py-1 rounded-lg bg-red-50 hover:bg-red-100 border border-red-200 text-red-700 text-[11px] font-bold transition-all flex items-center gap-1 cursor-pointer"
                          title="Blacklist email address"
                        >
                          <Ban className="w-3 h-3 text-red-600" />
                          Blacklist
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : activeTab === 'invites' ? (
            /* Pending Invitations Table */
            filteredInvites.length === 0 ? (
              <div className="bg-[#FFFDF9] border border-[#E4D8C8] rounded-2xl p-12 text-center shadow-earth">
                <Clock className="w-12 h-12 text-[#DDD0BE] mx-auto mb-3" />
                <h3 className="text-sm font-bold text-[#243238]">No Pending Invitations</h3>
                <p className="text-xs text-[#6F706C] mt-1">All sent mentor invitations have been accepted, expired, or revoked.</p>
              </div>
            ) : (
              <div className="bg-[#FFFDF9] border border-[#E4D8C8] rounded-2xl shadow-earth overflow-hidden">
                <table className="w-full text-left text-xs text-[#243238]">
                  <thead className="bg-[#F7F2EA] border-b border-[#E4D8C8] text-[#8D8A83] font-bold uppercase tracking-wider text-[10px]">
                    <tr>
                      <th className="px-6 py-4">Mentor Name</th>
                      <th className="px-6 py-4">Invite Email</th>
                      <th className="px-6 py-4">Status</th>
                      <th className="px-6 py-4">Sent Date</th>
                      <th className="px-6 py-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E4D8C8]">
                    {filteredInvites.map((inv) => (
                      <tr key={inv.id} className="hover:bg-[#F7F2EA]/40 transition-colors">
                        <td className="px-6 py-4 font-bold">{inv.name || 'Invited Mentor'}</td>
                        <td className="px-6 py-4 font-mono text-[#5A4634]">{inv.email}</td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-0.5">
                            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-300 w-fit">
                              {inv.status}
                            </span>
                            {inv.days_remaining !== undefined && (
                              <span className="text-[10px] text-[#8D8A83]">
                                {inv.days_remaining > 0 ? `Expires in ${inv.days_remaining}d` : 'Expired'}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-[#6F706C]">
                          {new Date(inv.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={async () => {
                                try {
                                  await SuperAdminService.inviteSthitihMentor({
                                    name: inv.name,
                                    email: inv.email,
                                    mentor_type: 'industry',
                                  });
                                  toast.success(`Resent invitation to ${inv.email}`);
                                } catch (e: any) {
                                  toast.error('Resend failed');
                                }
                              }}
                              className="px-3 py-1.5 rounded-lg bg-[#F7F2EA] hover:bg-[#EFE5D6] border border-[#DDD0BE] text-[11px] font-bold text-[#1F5A43] cursor-pointer flex items-center gap-1"
                            >
                              <Mail className="w-3 h-3" />
                              Resend
                            </button>
                            <button
                              onClick={() => setInvitationToRevoke(inv)}
                              className="px-3 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 border border-red-200 text-[11px] font-bold text-red-700 cursor-pointer flex items-center gap-1"
                            >
                              <Trash2 className="w-3 h-3 text-red-600" />
                              Revoke
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : (
            /* Blacklisted Emails Table */
            filteredBlacklist.length === 0 ? (
              <div className="bg-[#FFFDF9] border border-[#E4D8C8] rounded-2xl p-12 text-center shadow-earth">
                <ShieldAlert className="w-12 h-12 text-[#DDD0BE] mx-auto mb-3" />
                <h3 className="text-sm font-bold text-[#243238]">No Blacklisted Emails</h3>
                <p className="text-xs text-[#6F706C] mt-1 max-w-sm mx-auto">
                  {searchQuery ? 'No blacklisted emails matched your search query.' : 'No email addresses have been restricted from Sthitih Mentorship.'}
                </p>
                <button
                  onClick={() => {
                    setBlacklistEmailInput('');
                    setBlacklistReasonInput('');
                    setIsBlacklistModalOpen(true);
                  }}
                  className="mt-4 px-4 py-2 rounded-xl bg-[#243238] text-white text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 cursor-pointer hover:bg-black"
                >
                  <Ban className="w-4 h-4" /> Blacklist An Email
                </button>
              </div>
            ) : (
              <div className="bg-[#FFFDF9] border border-[#E4D8C8] rounded-2xl shadow-earth overflow-hidden">
                <div className="p-4 bg-[#F7F2EA]/60 border-b border-[#E4D8C8] flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Ban className="w-4 h-4 text-red-600" />
                    <span className="text-xs font-bold text-[#243238]">Blacklisted Emails Registry</span>
                  </div>
                  <button
                    onClick={() => {
                      setBlacklistEmailInput('');
                      setBlacklistReasonInput('');
                      setIsBlacklistModalOpen(true);
                    }}
                    className="px-3.5 py-1.5 rounded-xl bg-red-600 text-white text-xs font-bold uppercase tracking-wider hover:bg-red-700 flex items-center gap-1.5 cursor-pointer shadow-xs"
                  >
                    <Ban className="w-3.5 h-3.5" /> Blacklist An Email
                  </button>
                </div>
                <table className="w-full text-left text-xs text-[#243238]">
                  <thead className="bg-[#F7F2EA] border-b border-[#E4D8C8] text-[#8D8A83] font-bold uppercase tracking-wider text-[10px]">
                    <tr>
                      <th className="px-6 py-4">Blacklisted Email</th>
                      <th className="px-6 py-4">Reason / Policy Violation</th>
                      <th className="px-6 py-4">Blacklisted By</th>
                      <th className="px-6 py-4">Date Blacklisted</th>
                      <th className="px-6 py-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E4D8C8]">
                    {filteredBlacklist.map((item) => (
                      <tr key={item.id} className="hover:bg-[#F7F2EA]/40 transition-colors">
                        <td className="px-6 py-4 font-mono font-bold text-red-900 flex items-center gap-2">
                          <Ban className="w-3.5 h-3.5 text-red-600 shrink-0" />
                          {item.email}
                        </td>
                        <td className="px-6 py-4 text-[#5A4634]">
                          {item.reason || <span className="text-[#8D8A83] italic">No reason specified</span>}
                        </td>
                        <td className="px-6 py-4 text-[#6F706C]">
                          <span className="px-2 py-0.5 rounded-md bg-[#EFE5D6] text-[#5A4634] text-[10px] font-semibold">
                            {item.blacklisted_by}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-[#6F706C]">
                          {new Date(item.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={() => handleRemoveBlacklist(item.id, item.email)}
                            className="px-3 py-1.5 rounded-lg bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-[11px] font-bold text-emerald-800 cursor-pointer"
                          >
                            Unblock
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </main>
      </div>

      {/* MODAL 1: Invite Sthitih Mentor */}
      {isInviteModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-[#FFFDF9] border border-[#E4D8C8] rounded-2xl max-w-xl w-full p-6 shadow-earth-lg space-y-5 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between pb-3 border-b border-[#E4D8C8]">
              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-xl bg-[#1F5A43]/10 text-[#1F5A43]">
                  <Award className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-[#243238]">Invite Sthitih Industry Mentor</h3>
                  <p className="text-[11px] text-[#6F706C]">Send an official platform invitation to join as a Global Mentor.</p>
                </div>
              </div>
              <button
                onClick={() => setIsInviteModalOpen(false)}
                className="p-1.5 rounded-lg text-[#8D8A83] hover:text-[#243238] hover:bg-[#F7F2EA]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSendInvite} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-[#4A5558] uppercase tracking-wider mb-1">
                  Mentor Full Name *
                </label>
                <input
                  type="text"
                  required
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  placeholder="e.g. Dr. Jane Doe or Alex Smith"
                  className="w-full px-3.5 py-2.5 bg-[#F7F2EA] border border-[#DDD0BE] rounded-xl text-xs text-[#243238] placeholder-[#8D8A83] focus:outline-none focus:border-[#1F5A43]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-[#4A5558] uppercase tracking-wider mb-1">
                  Mentor Corporate / Professional Email *
                </label>
                <input
                  type="email"
                  required
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="e.g. jane.doe@techcompany.com"
                  className="w-full px-3.5 py-2.5 bg-[#F7F2EA] border border-[#DDD0BE] rounded-xl text-xs text-[#243238] placeholder-[#8D8A83] focus:outline-none focus:border-[#1F5A43]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-[#4A5558] uppercase tracking-wider mb-1">
                  Custom Welcome Note (Optional)
                </label>
                <textarea
                  value={inviteMessage}
                  onChange={(e) => setInviteMessage(e.target.value)}
                  rows={2}
                  placeholder="We are thrilled to welcome you as a distinguished Sthitih Industry Mentor..."
                  className="w-full px-3.5 py-2.5 bg-[#F7F2EA] border border-[#DDD0BE] rounded-xl text-xs text-[#243238] placeholder-[#8D8A83] focus:outline-none focus:border-[#1F5A43] resize-none"
                />
              </div>

              {/* Initial Client Grants */}
              <div>
                <label className="block text-xs font-bold text-[#4A5558] uppercase tracking-wider mb-1">
                  Pre-Grant Institution Access (Optional)
                </label>
                <p className="text-[10px] text-[#6F706C] mb-2">
                  Select institutions that should immediately have access to this mentor upon signup:
                </p>
                <div className="max-h-36 overflow-y-auto border border-[#DDD0BE] rounded-xl p-2 bg-[#F7F2EA] space-y-1.5 divide-y divide-[#DDD0BE]/40">
                  {clients.length === 0 ? (
                    <div className="text-xs text-[#8D8A83] p-2">No institutions registered yet.</div>
                  ) : (
                    clients.map((c) => {
                      const isSelected = selectedClientIds.includes(c.id);
                      return (
                        <label
                          key={c.id}
                          className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-[#FFFDF9] cursor-pointer text-xs font-semibold text-[#243238]"
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {
                              setSelectedClientIds(prev => 
                                isSelected ? prev.filter(id => id !== c.id) : [...prev, c.id]
                              );
                            }}
                            className="rounded border-[#DDD0BE] text-[#1F5A43] focus:ring-[#1F5A43]"
                          />
                          <span>{c.name}</span>
                          <span className="text-[10px] font-mono text-[#8D8A83] ml-auto">{c.code}</span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="pt-3 border-t border-[#E4D8C8] flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsInviteModalOpen(false)}
                  className="px-4 py-2 rounded-xl border border-[#DDD0BE] text-xs font-bold text-[#6F706C] hover:bg-[#F7F2EA]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingInvite}
                  className="px-5 py-2 rounded-xl bg-[#1F5A43] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#184735] disabled:opacity-50 flex items-center gap-2"
                >
                  {submittingInvite ? (
                    <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Mail className="w-4 h-4" />
                  )}
                  Send Invitation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 2: Manage Institution Access */}
      {isGrantModalOpen && selectedMentor && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-[#FFFDF9] border border-[#E4D8C8] rounded-2xl max-w-xl w-full p-6 shadow-earth-lg space-y-5 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between pb-3 border-b border-[#E4D8C8]">
              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-xl bg-[#C47C14]/10 text-[#C47C14]">
                  <Building2 className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-[#243238]">Grant Institution Access</h3>
                  <p className="text-[11px] text-[#6F706C]">
                    Configure which institutions can utilize <strong className="text-[#243238]">{selectedMentor.display_name}</strong>'s mentorship.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsGrantModalOpen(false)}
                className="p-1.5 rounded-lg text-[#8D8A83] hover:text-[#243238] hover:bg-[#F7F2EA]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#8D8A83]" />
                <input
                  type="text"
                  value={clientSearchQuery}
                  onChange={(e) => setClientSearchQuery(e.target.value)}
                  placeholder="Filter institutions..."
                  className="w-full pl-9 pr-4 py-2 bg-[#F7F2EA] border border-[#DDD0BE] rounded-xl text-xs text-[#243238] placeholder-[#8D8A83] focus:outline-none focus:border-[#1F5A43]"
                />
              </div>

              <div className="max-h-60 overflow-y-auto border border-[#DDD0BE] rounded-xl p-2 bg-[#F7F2EA] space-y-1.5">
                {clients
                  .filter(c => (c.name || '').toLowerCase().includes(clientSearchQuery.toLowerCase()))
                  .map((c) => {
                    const isGranted = mentorGrantedClientIds.includes(c.id);
                    return (
                      <div
                        key={c.id}
                        onClick={() => handleToggleClientGrant(c.id)}
                        className={`flex items-center justify-between p-2.5 rounded-xl border transition-all cursor-pointer ${
                          isGranted
                            ? 'bg-[#DFF5E7] border-[#1F7A4F]/30 text-[#1F7A4F]'
                            : 'bg-[#FFFDF9] border-[#E4D8C8] text-[#243238] hover:border-[#DDD0BE]'
                        }`}
                      >
                        <div className="flex items-center gap-2.5">
                          <input
                            type="checkbox"
                            checked={isGranted}
                            onChange={() => {}}
                            className="rounded border-[#DDD0BE] text-[#1F5A43] focus:ring-[#1F5A43]"
                          />
                          <div>
                            <span className="text-xs font-bold block">{c.name}</span>
                            <span className="text-[10px] text-[#8D8A83] font-mono">{c.code}</span>
                          </div>
                        </div>

                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                          isGranted ? 'bg-[#1F7A4F] text-white' : 'bg-[#F7F2EA] text-[#8D8A83]'
                        }`}>
                          {isGranted ? 'Access Granted' : 'No Access'}
                        </span>
                      </div>
                    );
                  })}
              </div>
            </div>

            <div className="pt-3 border-t border-[#E4D8C8] flex items-center justify-between">
              <span className="text-xs text-[#6F706C] font-semibold">
                {mentorGrantedClientIds.length} institution(s) selected
              </span>

              <div className="flex items-center gap-2.5">
                <button
                  type="button"
                  onClick={() => setIsGrantModalOpen(false)}
                  className="px-4 py-2 rounded-xl border border-[#DDD0BE] text-xs font-bold text-[#6F706C] hover:bg-[#F7F2EA]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveGrantInstitutes}
                  disabled={submittingGrant}
                  className="px-5 py-2 rounded-xl bg-[#1F5A43] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#184735] disabled:opacity-50 flex items-center gap-2"
                >
                  {submittingGrant ? (
                    <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4" />
                  )}
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 3: Revoke Invitation Confirmation */}
      {invitationToRevoke && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-[#FFFDF9] border border-red-200 rounded-2xl max-w-md w-full p-6 shadow-earth-lg space-y-4 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-red-100 text-red-700">
                <Trash2 className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-bold text-[#243238]">Revoke Invitation?</h3>
                <p className="text-xs text-[#6F706C]">This action cannot be undone.</p>
              </div>
            </div>

            <p className="text-xs text-[#5A4634] leading-relaxed bg-[#F7F2EA] p-3 rounded-xl border border-[#DDD0BE]">
              Are you sure you want to revoke the pending invitation for <strong className="text-red-900">{invitationToRevoke.email}</strong>? The invitation token will immediately expire and cannot be accepted.
            </p>

            <div className="pt-2 flex items-center justify-end gap-2.5">
              <button
                type="button"
                onClick={() => setInvitationToRevoke(null)}
                className="px-4 py-2 rounded-xl border border-[#DDD0BE] text-xs font-bold text-[#6F706C] hover:bg-[#F7F2EA]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRevokeInvite}
                disabled={submittingRevoke}
                className="px-5 py-2 rounded-xl bg-red-600 text-white text-xs font-bold uppercase tracking-wider hover:bg-red-700 disabled:opacity-50 flex items-center gap-2 cursor-pointer shadow-xs"
              >
                {submittingRevoke ? (
                  <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                Revoke Invitation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 4: Remove Mentor Confirmation */}
      {mentorToRemove && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-[#FFFDF9] border border-amber-200 rounded-2xl max-w-md w-full p-6 shadow-earth-lg space-y-4 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-amber-100 text-amber-800">
                <UserX className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-bold text-[#243238]">Remove Sthitih Mentor?</h3>
                <p className="text-xs text-[#6F706C]">Revokes platform mentorship privileges.</p>
              </div>
            </div>

            <p className="text-xs text-[#5A4634] leading-relaxed bg-[#F7F2EA] p-3 rounded-xl border border-[#DDD0BE]">
              Are you sure you want to remove <strong className="text-[#243238]">{mentorToRemove.display_name}</strong> (<span className="font-mono">{mentorToRemove.email}</span>) from the Sthitih Mentors program? This will revoke all granted institution access and unassign any active team mentoring.
            </p>

            <div className="pt-2 flex items-center justify-end gap-2.5">
              <button
                type="button"
                onClick={() => setMentorToRemove(null)}
                className="px-4 py-2 rounded-xl border border-[#DDD0BE] text-xs font-bold text-[#6F706C] hover:bg-[#F7F2EA]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRemoveMentor}
                disabled={submittingRemoveMentor}
                className="px-5 py-2 rounded-xl bg-amber-700 text-white text-xs font-bold uppercase tracking-wider hover:bg-amber-800 disabled:opacity-50 flex items-center gap-2 cursor-pointer shadow-xs"
              >
                {submittingRemoveMentor ? (
                  <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                ) : (
                  <UserX className="w-4 h-4" />
                )}
                Remove Mentor
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 5: Blacklist Email Address */}
      {isBlacklistModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-[#FFFDF9] border border-red-300 rounded-2xl max-w-md w-full p-6 shadow-earth-lg space-y-4 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between pb-3 border-b border-[#E4D8C8]">
              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-xl bg-red-100 text-red-700">
                  <Ban className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-[#243238]">Blacklist Email Address</h3>
                  <p className="text-[11px] text-[#6F706C]">Prohibit onboarding and revoke active privileges.</p>
                </div>
              </div>
              <button
                onClick={() => setIsBlacklistModalOpen(false)}
                className="p-1.5 rounded-lg text-[#8D8A83] hover:text-[#243238] hover:bg-[#F7F2EA]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleBlacklistSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-[#243238] mb-1">
                  Email Address <span className="text-red-600">*</span>
                </label>
                <input
                  type="email"
                  required
                  value={blacklistEmailInput}
                  onChange={(e) => setBlacklistEmailInput(e.target.value)}
                  placeholder="mentor@example.com"
                  className="w-full px-3.5 py-2 bg-[#F7F2EA] border border-[#DDD0BE] rounded-xl text-xs text-[#243238] placeholder-[#8D8A83] focus:outline-none focus:border-red-600"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-[#243238] mb-1">
                  Reason for Blacklisting (Optional)
                </label>
                <textarea
                  rows={3}
                  value={blacklistReasonInput}
                  onChange={(e) => setBlacklistReasonInput(e.target.value)}
                  placeholder="e.g. Repeated no-shows, policy violations, or requested removal..."
                  className="w-full px-3.5 py-2 bg-[#F7F2EA] border border-[#DDD0BE] rounded-xl text-xs text-[#243238] placeholder-[#8D8A83] focus:outline-none focus:border-red-600 resize-none"
                />
              </div>

              <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-[11px] text-red-900 leading-relaxed">
                <strong>Notice:</strong> This will revoke any active Sthitih Mentor role, revoke pending invitations, and prevent anyone using this email from being invited.
              </div>

              <div className="pt-2 flex items-center justify-end gap-2.5">
                <button
                  type="button"
                  onClick={() => setIsBlacklistModalOpen(false)}
                  className="px-4 py-2 rounded-xl border border-[#DDD0BE] text-xs font-bold text-[#6F706C] hover:bg-[#F7F2EA]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingBlacklist}
                  className="px-5 py-2 rounded-xl bg-red-600 text-white text-xs font-bold uppercase tracking-wider hover:bg-red-700 disabled:opacity-50 flex items-center gap-2 cursor-pointer shadow-xs"
                >
                  {submittingBlacklist ? (
                    <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Ban className="w-4 h-4" />
                  )}
                  Confirm Blacklist
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SthitihMentorsView;
